import React, { useMemo, useEffect, useCallback, useState } from 'react';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../stores/store';
import { useAssemblyStore } from '../../stores/AssemblyStore';

import { useDragEndHandler } from '../../hooks/useDragEndHandler';
import { useSimpleDragManager } from './SimpleDragManager';
import { SVGShapeNew } from './SVGShapeNew';
import { useSelectionStore } from '../../stores/SelectionStore';

import { AssemblyStyle } from './SVGShapeNew';
import { Point } from '../../types/Point';
import { TRANSPARENT_COLOR } from '../../utils/constants';
import { normalizeColor } from '../../utils/colorUtils';
import { TakeoffAssembly } from '../../types/AssemblyItemType';
import { useFetchCountedAssemblies } from '../../hooks/useFetchCountedAssemblies';
import { useSubmitTakeoffAssembly } from '../../hooks/useSubmitTakeoffAssembly';
import { markAssemblyUpdating, markAssembliesUpdated } from '../../utils/assemblyUpdateTracker';

interface SelectedPoint {
  pointId: string;
  assemblyId: string;
  originalX: number;
  originalY: number;
}

export interface SVGPointsNewProps {
  cursorPositionRef: React.MutableRefObject<{ x: number; y: number }>;
  selectedPoints?: SelectedPoint[];
}

/**
 * Optimized SVGPoints component that uses DragManager for efficient drag operations
 * Separates visual updates from store mutations for better performance
 */
export const SVGPointsNew: React.FC<SVGPointsNewProps> = ({
  cursorPositionRef,
  selectedPoints = [],
}) => {
  const {
    selectedObject,
    isDragging,
    isEditingPointFillColor,
    isEditingPointBorderColor,
    isEditingLineColor,
  } = useStore(useUnityBuildStore);

  const { data: filteredCountedAssemblies } = useFetchCountedAssemblies();
  const { currentTakeoffAssembly } = useStore(useAssemblyStore);
  const { isDragging: isDraggingPoints, updateDrag } = useSimpleDragManager();
  const { handleDragEnd } = useDragEndHandler();
  const { submitAssembly } = useSubmitTakeoffAssembly();
  const userScale = useStore(useUnityBuildStore).userScale;

  // Fallback to global selection store when prop not provided
  const selectedPointsFromStore = useStore(useSelectionStore).selectedPoints;
  const effectiveSelectedPoints =
    selectedPoints.length > 0 ? selectedPoints : selectedPointsFromStore;

  // Local tick to force re-render during coordinated drags initiated elsewhere
  const [, setCoordinatedDragTick] = useState(0);

  // Force re-render when simple drag manager updates
  const [dragUpdateTick, setDragUpdateTick] = useState(0);

  // Handle global mouse events for drag
  useEffect(() => {
    if (!isDraggingPoints) return;

    const handleMouseMove = (e: MouseEvent) => {
      // Use cursorPositionRef which is already updated by useMouseEvents
      // This avoids coordinate transformation issues and reduces jitter
      updateDrag(cursorPositionRef.current);
      setDragUpdateTick(t => t + 1);
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (e.button === 0) {
        // Mark all affected assemblies as updating for a consistent UX
        try {
          const dragState = useSimpleDragManager.getState();
          const affectedAssemblyIds = new Set<string>();
          dragState.liveEditingPositions.forEach((_pos, pointId) => {
            const sel = effectiveSelectedPoints.find(p => p.pointId === pointId);
            if (sel) affectedAssemblyIds.add(sel.assemblyId);
          });
          affectedAssemblyIds.forEach(id => markAssemblyUpdating(id));
        } catch {}

        handleDragEnd();
        // We clear flags after submit completes via submit hook; fallback timeout just in case
        setTimeout(() => {
          try {
            const dragState = useSimpleDragManager.getState();
            const affectedAssemblyIds = new Set<string>();
            dragState.liveEditingPositions.forEach((_pos, pointId) => {
              const sel = effectiveSelectedPoints.find(p => p.pointId === pointId);
              if (sel) affectedAssemblyIds.add(sel.assemblyId);
            });
            markAssembliesUpdated(Array.from(affectedAssemblyIds));
          } catch {}
        }, 1200);
        setDragUpdateTick(t => t + 1);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingPoints, handleDragEnd, updateDrag, cursorPositionRef]);

  // Listen for coordinated drag events from annotations
  useEffect(() => {
    const handleCoordinatedDragStart = (e: CustomEvent) => {
      const { mode } = e.detail;
      if (mode === 'group' && effectiveSelectedPoints.length > 0) {
        // Store initial positions of selected takeoff points for coordinated dragging
        const initialPositions = new Map();
        effectiveSelectedPoints.forEach(point => {
          initialPositions.set(point.pointId, {
            x: point.originalX,
            y: point.originalY,
          });
        });
        (window as any).__takeoffInitialPositions = initialPositions;
      }
    };

    const handleCoordinatedDragUpdate = (e: CustomEvent) => {
      const { offset } = e.detail;
      const initialPositions = (window as any).__takeoffInitialPositions;

      // If start was missed for any reason, initialize on first update
      if (!initialPositions && effectiveSelectedPoints.length > 0) {
        try {
          console.warn('[POINTS DRAG UPDATE] Missing start; initializing on the fly');
        } catch {}
        const init = new Map();
        effectiveSelectedPoints.forEach(point => {
          init.set(point.pointId, { x: point.originalX, y: point.originalY });
        });
        (window as any).__takeoffInitialPositions = init;
      }

      const initPos = (window as any).__takeoffInitialPositions;
      if (initPos && effectiveSelectedPoints.length > 0) {
        // Update live positions for all selected takeoff points
        const livePositions = new Map();
        effectiveSelectedPoints.forEach(point => {
          const initial = initPos.get(point.pointId);
          if (initial) {
            livePositions.set(point.pointId, {
              x: initial.x + offset.x,
              y: initial.y + offset.y,
            });
          }
        });
        (window as any).__takeoffLivePositions = livePositions;
        setCoordinatedDragTick(t => t + 1);
        setDragUpdateTick(t => t + 1); // Also trigger re-render for points
      }
    };

    const handleCoordinatedDragEnd = async (e: CustomEvent) => {
      // When drag is initiated by annotation, we need to persist takeoff points here
      // since SimpleDragManager.endDrag() was never called

      if (effectiveSelectedPoints.length > 0) {
        try {
          const livePositions = (window as any).__takeoffLivePositions;
          if (livePositions) {
            // Group points by assembly for batch updates
            const assemblyUpdates = new Map<string, any>();

            effectiveSelectedPoints.forEach(selectedPoint => {
              if (!livePositions.has(selectedPoint.pointId)) return;

              const livePos = livePositions.get(selectedPoint.pointId);
              const assemblyId = selectedPoint.assemblyId;

              if (!assemblyUpdates.has(assemblyId)) {
                // Find the assembly (current page only)
                const allAssemblies =
                  filteredCountedAssemblies?.filteredAssemblies?.flatMap(
                    (group: any) => group.assembliesList || []
                  ) || [];
                const assembly = allAssemblies.find((a: any) => a.assemblyid === assemblyId);
                if (assembly) {
                  assemblyUpdates.set(assemblyId, {
                    assembly: { ...assembly, pointsList: [...(assembly.pointsList || [])] },
                    updates: [],
                  });
                }
              }

              assemblyUpdates.get(assemblyId)?.updates.push({
                pointId: selectedPoint.pointId,
                newX: Math.round(livePos.x),
                newY: Math.round(livePos.y),
              });
            });

            // Submit each updated assembly
            const updatePromises = [];
            for (const [, { assembly, updates }] of assemblyUpdates) {
              // Apply updates to assembly points
              const updatedPointsList = assembly.pointsList.map((point: any) => {
                const update = updates.find((u: any) => u.pointId === point.pointid);
                if (update) {
                  return { ...point, x: update.newX, y: update.newY };
                }
                return point;
              });

              const updatedAssembly = { ...assembly, pointsList: updatedPointsList };

              updatePromises.push(
                submitAssembly({
                  shouldUpdateBreakout: false,
                  assembly: updatedAssembly,
                  isUpdating: true,
                  pageScale: userScale,
                })
              );
            }

            await Promise.all(updatePromises);
          }
        } catch (err) {
          console.error('Failed to persist takeoff points in coordinated drag:', err);
        }
      }

      // Clear selections and cleanup after a delay to allow backend updates
      setTimeout(() => {
        const selectionStore = useSelectionStore.getState();
        selectionStore.setSelectedPoints([]);

        const annotationStore = (
          require('../annotations/annotationStore') as any
        ).useAnnotationStore?.getState?.();
        annotationStore?.clearSelectedShapeIds?.();

        // Clear current takeoff assembly to prevent hanging preview
        const assemblyStore = useAssemblyStore.getState();
        assemblyStore.setCurrentTakeoffAssembly(null);

        // Clean up live positions after backend has time to update
        (window as any).__takeoffInitialPositions = null;
        (window as any).__takeoffLivePositions = null;
        setCoordinatedDragTick(t => t + 1);
      }, 200); // Longer delay to ensure backend updates complete
    };

    document.addEventListener(
      'coordinated:drag-start',
      handleCoordinatedDragStart as EventListener
    );
    document.addEventListener(
      'coordinated:drag-update',
      handleCoordinatedDragUpdate as EventListener
    );
    document.addEventListener('coordinated:drag-end', handleCoordinatedDragEnd as any);

    return () => {
      document.removeEventListener(
        'coordinated:drag-start',
        handleCoordinatedDragStart as EventListener
      );
      document.removeEventListener(
        'coordinated:drag-update',
        handleCoordinatedDragUpdate as EventListener
      );
      document.removeEventListener('coordinated:drag-end', handleCoordinatedDragEnd as any);
    };
  }, [effectiveSelectedPoints, filteredCountedAssemblies, submitAssembly, userScale]);

  // Create a memoized lookup set of selected point IDs
  const selectedPointsSet = useMemo(() => {
    return new Set(effectiveSelectedPoints.map(sp => sp.pointId));
  }, [effectiveSelectedPoints]);

  // Helper to check if a point is selected
  const isPointSelected = useCallback(
    (pointId: string) => selectedPointsSet.has(pointId),
    [selectedPointsSet]
  );

  // Helper to get effective position (live position during any drag, or original position)
  const getEffectivePosition = useCallback(
    (point: Point) => {
      // First check for coordinated drag live positions
      const livePositions = (window as any).__takeoffLivePositions;
      if (livePositions && livePositions.has(point.pointid)) {
        const livePos = livePositions.get(point.pointid);
        return { x: livePos.x, y: livePos.y };
      }

      // Then check for simple drag manager live positions
      const dragManager = useSimpleDragManager.getState();
      if (dragManager.isDragging && point.pointid) {
        const dragPos = dragManager.getPointPosition(point.pointid);
        if (dragPos) {
          return { x: dragPos.x, y: dragPos.y };
        }
      }

      return { x: point.x, y: point.y };
    },
    [
      /* force recompute on drag updates */
    ]
  );

  // Unified assembly points rendering
  const assemblyPoints = useMemo(() => {
    if (
      !selectedObject ||
      !filteredCountedAssemblies ||
      Array.isArray(filteredCountedAssemblies) ||
      !filteredCountedAssemblies.filteredAssemblies
    ) {
      return null;
    }

    const allAssemblies = filteredCountedAssemblies.filteredAssemblies.flatMap(
      (group: any) => group.assembliesList || []
    );

    // Check if currentTakeoffAssembly exists but doesn't have a matching assembly in filteredCountedAssemblies
    // This happens when selecting from FolderNodes (new assembly creation)
    const hasMatchingAssembly =
      currentTakeoffAssembly &&
      allAssemblies.some(
        (assembly: any) => assembly.assemblyid === currentTakeoffAssembly.assemblyid
      );

    // Always include existing filtered assemblies, and add currentTakeoffAssembly if it's new
    const assembliesToRender = hasMatchingAssembly
      ? allAssemblies
      : currentTakeoffAssembly
        ? [...allAssemblies, currentTakeoffAssembly] // Show both existing and new assembly
        : allAssemblies;

    return assembliesToRender.map((assembly: TakeoffAssembly, assemblyIndex: number) => {
      const isCurrentAssembly = assembly.assemblyid === currentTakeoffAssembly?.assemblyid;

      // UNIFIED STATE: Use filteredCountedAssemblies as base, overlay currentTakeoffAssembly changes
      let effectiveAssembly = assembly;
      let effectivePoints = assembly.pointsList || [];

      // Apply currentTakeoffAssembly overlay only when actively editing
      if (isCurrentAssembly && currentTakeoffAssembly) {
        effectiveAssembly = { ...assembly, ...currentTakeoffAssembly };
        effectivePoints = currentTakeoffAssembly.pointsList || assembly.pointsList || [];
      }

      return effectivePoints?.map((point: Point, index: number) => {
        const isFaint = point.isincludedincount === false;
        const isSelected = isPointSelected(point.pointid);

        // Determine colors based on selection state
        let pointBorderColor = isFaint
          ? TRANSPARENT_COLOR
          : normalizeColor(effectiveAssembly?.pointbordercolor, '#000000');
        let pointFillColor = isFaint
          ? TRANSPARENT_COLOR
          : normalizeColor(effectiveAssembly?.pointfillcolor, '#f44336');
        let fillOpacity = 0.5;

        if (isSelected) {
          pointBorderColor = '#4285F4';
          pointFillColor = '#4285F4';
          fillOpacity = 0.7;
        }

        const assemblyStyle: AssemblyStyle = {
          pointtype: effectiveAssembly?.pointtype || 'circle',
          pointsize: Number(effectiveAssembly?.pointsize) || 5,
          pointbordercolor: pointBorderColor,
          pointfillcolor: pointFillColor,
          linedasharray: effectiveAssembly?.linedasharray || '',
          linesize: Number(effectiveAssembly?.linesize) || 3,
          linecolor: effectiveAssembly?.linecolor || 'blue',
          isselected: isCurrentAssembly || isSelected,
          fillopacity: fillOpacity,
        };

        // Get effective position (live position during coordinated drag, or original position)
        const effectivePosition = getEffectivePosition(point);
        const pointWithEffectivePosition = {
          ...point,
          x: effectivePosition.x,
          y: effectivePosition.y,
        };

        return (
          <SVGShapeNew
            key={point.pointid}
            point={pointWithEffectivePosition}
            pointIndex={index}
            assemblyIndex={assemblyIndex}
            assemblyId={assembly.assemblyid}
            assemblyStyle={assemblyStyle}
            cursorPositionRef={cursorPositionRef}
            verticalLength={point.verticallength || 0}
          />
        );
      });
    });
  }, [
    selectedObject,
    filteredCountedAssemblies,
    currentTakeoffAssembly,
    cursorPositionRef,
    isPointSelected,
    getEffectivePosition,
    dragUpdateTick, // Force re-render on drag updates
  ]);

  // Remove duplicate rendering - unified approach handles this above

  return <>{assemblyPoints}</>;
};
